import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, MenuItem, Paper, TextField, Typography } from '@mui/material';
import { AuthService } from '../../services/auth.service';
import { MaintenanceService } from '../../services/maintenance.service';
import { DepartmentService } from '../../services/department.service';
import { FixedAssetService } from '../../services/fixedAsset.service';
import { useToast } from '../../context/ToastContext';
import SearchableSelect from '../common/SearchableSelect';
import {
    CRITICALITIES, Criticality, EQUIPMENT_OWNERSHIPS, EquipmentDTO, EquipmentOwnership, EquipmentRequest,
    EquipmentTypeDTO, mntLabel,
} from '../../interfaces/maintenance';
import { Department } from '../../interfaces/department.interface';

interface AssetOption { id: string; assetCode: string; name: string }

const field = { flex: '1 1 240px', maxWidth: 420, minWidth: 0 };

/** Create / edit equipment. Status is not editable here (moves and status changes are separate actions). */
const EquipmentForm: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const isEdit = !!id;
    const navigate = useNavigate();
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;

    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [types, setTypes] = useState<EquipmentTypeDTO[]>([]);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [assets, setAssets] = useState<AssetOption[]>([]);
    const [parents, setParents] = useState<EquipmentDTO[]>([]);

    const [code, setCode] = useState('');
    const [name, setName] = useState('');
    const [typeId, setTypeId] = useState('');
    const [manufacturer, setManufacturer] = useState('');
    const [model, setModel] = useState('');
    const [serialNo, setSerialNo] = useState('');
    const [criticality, setCriticality] = useState<Criticality>('B');
    const [ownership, setOwnership] = useState<EquipmentOwnership>('OWNED');
    const [ownerParty, setOwnerParty] = useState('');
    const [rentalUntil, setRentalUntil] = useState('');
    const [departmentId, setDepartmentId] = useState('');
    const [parentId, setParentId] = useState('');
    const [fixedAssetId, setFixedAssetId] = useState('');
    const [commissionedOn, setCommissionedOn] = useState('');
    const [warrantyUntil, setWarrantyUntil] = useState('');
    const [notes, setNotes] = useState('');

    useEffect(() => {
        MaintenanceService.listTypes(institutionId).then(t => setTypes(t.filter(x => x.active))).catch(e => toast.showApiError(e, 'Failed to load types'));
        DepartmentService.getLocationOptions().then(r => setDepartments(r.content ?? [])).catch(() => setDepartments([]));
        FixedAssetService.listByInstitution(institutionId, 0, 200).then(r => setAssets(r.content as AssetOption[])).catch(() => setAssets([]));
        MaintenanceService.listEquipment(institutionId, {}, 0, 200).then(r => setParents(r.content)).catch(() => setParents([]));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!id) return;
        MaintenanceService.getEquipment(id).then(e => {
            setCode(e.code); setName(e.name); setTypeId(e.equipmentTypeId);
            setManufacturer(e.manufacturer ?? ''); setModel(e.model ?? ''); setSerialNo(e.serialNo ?? '');
            setCriticality(e.criticality); setOwnership(e.ownership); setOwnerParty(e.ownerParty ?? '');
            setRentalUntil(e.rentalUntil ?? ''); setDepartmentId(e.locationDepartmentId ?? '');
            setParentId(e.parentEquipmentId ?? ''); setFixedAssetId(e.fixedAssetId ?? '');
            setCommissionedOn(e.commissionedOn ?? ''); setWarrantyUntil(e.warrantyUntil ?? ''); setNotes(e.notes ?? '');
        }).catch(e => toast.showApiError(e, 'Failed to load equipment')).finally(() => setLoading(false));
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const nul = (v: string) => (v.trim() === '' ? null : v.trim());

    const handleSave = async () => {
        if (!code.trim() || !name.trim() || !typeId) {
            toast.showError('Code, name and type are required');
            return;
        }
        const req: EquipmentRequest = {
            institutionId, code: code.trim(), name: name.trim(), equipmentTypeId: typeId,
            manufacturer: nul(manufacturer), model: nul(model), serialNo: nul(serialNo),
            criticality, ownership, ownerParty: nul(ownerParty), rentalUntil: nul(rentalUntil),
            locationDepartmentId: departmentId || null, parentEquipmentId: parentId || null,
            fixedAssetId: fixedAssetId || null, commissionedOn: nul(commissionedOn),
            warrantyUntil: nul(warrantyUntil), notes: nul(notes),
        };
        try {
            setSaving(true);
            const saved = isEdit ? await MaintenanceService.updateEquipment(id!, req) : await MaintenanceService.createEquipment(req);
            toast.showSuccess(isEdit ? 'Equipment updated' : 'Equipment created');
            navigate(`/maintenance/equipment/${saved.id}`);
        } catch (e) {
            toast.showApiError(e, 'Failed to save equipment');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

    return (
        <Box sx={{ p: { xs: 1, sm: 3 } }} data-help-page="maint.equipmentForm.page">
            <Typography variant="h5" sx={{ mb: 2 }}>{isEdit ? 'Edit equipment' : 'New equipment'}</Typography>
            <Paper sx={{ p: { xs: 2, sm: 3 } }} data-help-section="maint.equipmentForm.form">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <TextField required size="small" label="Code" data-help="maint.equipmentForm.field.code" value={code} onChange={e => setCode(e.target.value)} sx={field} />
                    <TextField required size="small" label="Name" data-help="maint.equipmentForm.field.name" value={name} onChange={e => setName(e.target.value)} sx={field} />
                    <Box sx={field}>
                        <SearchableSelect
                            options={types} value={typeId} required size="small" label="Equipment type" data-help="maint.equipmentForm.field.type"
                            getOptionLabel={t => `${t.name} (${t.code})`} onChange={setTypeId}
                        />
                    </Box>
                    <TextField size="small" label="Manufacturer" data-help="maint.equipmentForm.field.manufacturer" value={manufacturer} onChange={e => setManufacturer(e.target.value)} sx={field} />
                    <TextField size="small" label="Model" data-help="maint.equipmentForm.field.model" value={model} onChange={e => setModel(e.target.value)} sx={field} />
                    <TextField size="small" label="Serial no." data-help="maint.equipmentForm.field.serialNo" value={serialNo} onChange={e => setSerialNo(e.target.value)} sx={field} />
                    <TextField select size="small" label="Criticality" data-help="maint.equipmentForm.field.criticality" value={criticality} onChange={e => setCriticality(e.target.value as Criticality)} sx={field}>
                        {CRITICALITIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </TextField>
                    <TextField select size="small" label="Ownership" data-help="maint.equipmentForm.field.ownership" value={ownership} onChange={e => setOwnership(e.target.value as EquipmentOwnership)} sx={field}>
                        {EQUIPMENT_OWNERSHIPS.map(o => <MenuItem key={o} value={o}>{mntLabel(o)}</MenuItem>)}
                    </TextField>
                    {ownership !== 'OWNED' && (
                        <>
                            <TextField size="small" label="Owner / lessor" data-help="maint.equipmentForm.field.ownerParty" value={ownerParty} onChange={e => setOwnerParty(e.target.value)} sx={field} />
                            <TextField size="small" type="date" label="Rental until" data-help="maint.equipmentForm.field.rentalUntil" value={rentalUntil} onChange={e => setRentalUntil(e.target.value)} sx={field} InputLabelProps={{ shrink: true }} />
                        </>
                    )}
                    <Box sx={field}>
                        <SearchableSelect
                            options={departments} value={departmentId} size="small" label="Location (department)" data-help="maint.equipmentForm.field.location"
                            getOptionLabel={d => d.name} onChange={setDepartmentId}
                        />
                    </Box>
                    <Box sx={field}>
                        <SearchableSelect
                            options={parents.filter(p => p.id !== id && !p.parentEquipmentId)} value={parentId} size="small" label="Parent equipment" data-help="maint.equipmentForm.field.parent"
                            getOptionLabel={p => `${p.code} - ${p.name}`} onChange={setParentId}
                        />
                    </Box>
                    <Box sx={field}>
                        <SearchableSelect
                            options={assets} value={fixedAssetId} size="small" label="Fixed asset" data-help="maint.equipmentForm.field.fixedAsset"
                            getOptionLabel={a => `${a.assetCode} - ${a.name}`} onChange={setFixedAssetId}
                        />
                    </Box>
                    <TextField size="small" type="date" label="Commissioned on" data-help="maint.equipmentForm.field.commissionedOn" value={commissionedOn} onChange={e => setCommissionedOn(e.target.value)} sx={field} InputLabelProps={{ shrink: true }} />
                    <TextField size="small" type="date" label="Warranty until" data-help="maint.equipmentForm.field.warrantyUntil" value={warrantyUntil} onChange={e => setWarrantyUntil(e.target.value)} sx={field} InputLabelProps={{ shrink: true }} />
                    <TextField size="small" multiline minRows={2} label="Notes" data-help="maint.equipmentForm.field.notes" value={notes} onChange={e => setNotes(e.target.value)} sx={{ flex: '1 1 100%' }} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 3, flexWrap: 'wrap' }}>
                    <Button variant="contained" onClick={handleSave} disabled={saving} data-help="maint.equipmentForm.save">{saving ? 'Saving...' : 'Save'}</Button>
                    <Button onClick={() => navigate(isEdit ? `/maintenance/equipment/${id}` : '/maintenance/equipment')} data-help="maint.equipmentForm.cancel">Cancel</Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default EquipmentForm;
